import { BffApiClientMolecule } from "@/app/api-client/store"
import { NftSelectionMoleculeReatom } from "@/app/nft/sell/selection/nft-selection-molecule.reatom"
import { WalletConnectMoleculeReatom } from "@/app/wallet-connect/store.reatom"
import type { Bid } from "@anaconda/core/bid/types"
import type { CollectionId } from "@anaconda/core/nft-collection/types"
import type { NftId, NftMiniature } from "@anaconda/core/nft/types"
import type { PriceValue } from "@anaconda/core/price/types"
import { action, atom, computed } from "@reatom/core"
import Big from "big.js"
import { molecule, useMolecule } from "bunshi/react"
import { UserCollectionItemsMoleculeReatom } from "../items/user-items/store/user-collection-items-molecule.reatom"
import { getHighestCollectionOffersQueryParams } from "../offers/utils"
import { CollectionMoleculeReatom } from "../store/index.reatom"

export type OfferMatch = {
  offer?: Bid
  offersLoading: boolean
}

/**
 * Core molecule for matching NFTs to collection offers
 * Note: This returns computed atoms, actual query execution happens in components with TanStack Query
 */
export const SellCollectionNftMoleculeReatom = molecule((mol) => {
  const { selectedNftsAtom, addNft, removeNft } = mol(
    NftSelectionMoleculeReatom,
  )
  const { unionAddress } = mol(WalletConnectMoleculeReatom)

  // Store for collection offers query results
  // Components will populate this via TanStack Query
  const collectionOffersMap = atom<
    Record<
      CollectionId,
      {
        isLoading: boolean
        data?: { offers: Bid[] }
      }
    >
  >({}, "collectionOffersMap")

  // Match selected NFTs to available offers
  const offerByNft = computed(() => {
    const nftsToSell = selectedNftsAtom()
    const collectionOffersAlreadyMatched: Record<CollectionId, number> = {}
    const matchResult: Record<NftId, OfferMatch> = {}

    const collectionOffers = collectionOffersMap()
    const userWallet = unionAddress()

    for (const nft of nftsToSell) {
      const collectionId = nft.collection.id
      const collectionOffersQuery = collectionOffers[collectionId]

      // If there are no offers for this collection, skip it
      if (!collectionOffersQuery) continue

      // If the offers are loading, set the loading state
      if (collectionOffersQuery.isLoading) {
        matchResult[nft.id] = { offersLoading: true }
        continue
      }

      const alreadyMatchedOffers =
        collectionOffersAlreadyMatched[collectionId] ?? 0

      const nextOffer = collectionOffersQuery.data?.offers.filter((item) => {
        return item.order.maker !== userWallet
      })[alreadyMatchedOffers]

      // Increment the counter if there is an offer
      if (nextOffer) {
        collectionOffersAlreadyMatched[collectionId] = alreadyMatchedOffers + 1
      }

      matchResult[nft.id] = {
        offersLoading: false,
        offer: nextOffer,
      }
    }

    return matchResult
  }, "offerByNft")

  const readyToSell = computed(() => {
    const hasMatchedOffers = Object.keys(offerByNft()).length > 0
    return hasMatchedOffers
  }, "readyToSell")

  const sellPrice = computed(() => {
    const offersByNft = offerByNft()
    const result = Object.values(offersByNft).reduce<PriceValue | undefined>(
      (acc, { offer }) => {
        if (!offer) return acc

        if (!acc) return offer.price

        return Object.assign({}, acc, {
          amount: Big(acc.amount).add(offer.price.amount).toString(),
        })
      },
      undefined,
    )

    return result
  }, "sellPrice")

  const bestSellOffer = computed(() => {
    const offersByNft = offerByNft()
    const result = Object.values(offersByNft).reduce<Bid | undefined>(
      (acc, { offer }) => {
        if (!offer) return acc
        if (!acc) return offer

        return offer.price.amount > acc.price.amount ? offer : acc
      },
      undefined,
    )

    return result
  }, "bestSellOffer")

  // Action to update collection offers map (called from components)
  const setCollectionOffers = action(
    (
      collectionId: CollectionId,
      data: { isLoading: boolean; data?: { offers: Bid[] } },
    ) => {
      const prev = collectionOffersMap()[collectionId]
      // Skip no-op writes — new map object would re-run offerByNft and loop
      // subscribers inside reatomComponent.
      if (
        prev &&
        prev.isLoading === data.isLoading &&
        prev.data?.offers === data.data?.offers
      ) {
        return
      }

      collectionOffersMap.set({
        ...collectionOffersMap(),
        [collectionId]: data,
      })
    },
    "setCollectionOffers",
  )

  return {
    offerByNft,
    readyToSell,
    sellPrice,
    bestSellOffer,
    collectionOffersMap,
    setCollectionOffers,
  }
})

/**
 * Molecule for NFT sell offer logic specific to a collection
 * Handles toggling with offer availability checks
 */
export const NftSellOfferMoleculeReatom = molecule((mol) => {
  const { collectionId } = mol(CollectionMoleculeReatom)
  const { selectedNftsAtom, toggleNftSelection } = mol(
    NftSelectionMoleculeReatom,
  )
  const bffApiClient = mol(BffApiClientMolecule)
  const { offerByNft, collectionOffersMap } = mol(
    SellCollectionNftMoleculeReatom,
  )
  const { unionAddress } = mol(WalletConnectMoleculeReatom)

  if (collectionId === undefined) {
    throw new Error("Unknown collectionId")
  }

  // Query params for highest collection offers
  const highestCollectionOffersQueryParams = computed(() => {
    const queryParams = {
      ...getHighestCollectionOffersQueryParams(bffApiClient, collectionId),
      refetchInterval: 5000,
    }
    return queryParams
  }, "highestCollectionOffersQueryParams")

  // Check if there are offers available to match
  const hasOfferToMatch = computed(() => {
    const collectionOffers = collectionOffersMap()
    const alreadyMatchedOffers = offerByNft()
    const selectedNfts = selectedNftsAtom()
    const userWallet = unionAddress()

    const collectionOffersQuery = collectionOffers[collectionId]
    if (!collectionOffersQuery?.data) return false

    const offers = collectionOffersQuery.data.offers.filter((item) => {
      return item.order.maker !== userWallet
    })
    const availableOffersNumber = offers.length

    const alreadyMatchedCollectionOffersNumber = Object.keys(
      alreadyMatchedOffers,
    ).reduce((acc, nftId) => {
      const nft = selectedNfts.find((n) => n.id === nftId)
      if (nft?.collection.id === collectionId) return acc + 1
      return acc
    }, 0)

    return availableOffersNumber > alreadyMatchedCollectionOffersNumber
  }, "hasOfferToMatch")

  // Toggle NFT with offer availability check
  const toggleNft = action((nft: NftMiniature) => {
    const selectedNfts = selectedNftsAtom()
    const alreadyExists = selectedNfts.find((n) => n.id === nft.id)

    if (alreadyExists) {
      toggleNftSelection(nft)
      return
    }

    const canMatch = hasOfferToMatch()
    if (!canMatch) {
      return
    }

    toggleNftSelection(nft)
  }, "toggleNftWithOfferCheck")

  return {
    hasOfferToMatch,
    toggleNft,
    highestCollectionOffersQueryParams,
  }
})

export function useCollectionNftsSellState() {
  const sellMolecule = useMolecule(SellCollectionNftMoleculeReatom)
  const offerMolecule = useMolecule(NftSellOfferMoleculeReatom)

  return {
    offerByNftAtom: sellMolecule.offerByNft,
    readyToSellAtom: sellMolecule.readyToSell,
    sellPriceAtom: sellMolecule.sellPrice,
    bestSellOfferAtom: sellMolecule.bestSellOffer,
    toggleNftAtom: offerMolecule.toggleNft,
  }
}

export const NftSellOfferMolecule = NftSellOfferMoleculeReatom
